import './List.css';
import { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { Context } from '../../AuthContext';
import Card from '../Card/Card';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import AddViaLink from '../AddViaLink';
import WhatsComing from '../WhatsComing';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Spinner from '../Spinner';
import { useCategory } from '../../CategoryContext';
import { Button } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import WatchGuideLink from '../WatchGuideLink/WatchGuideLink';
import SortByMenu from '../SortByMenu/SortByMenu';
import { getSortOptions } from '../../utils/sortOptions';
import { toast } from 'react-toastify';

const List = ({
  category,
  heading,
  cardHeader,
  icons,
  urlIcons,
  dotColor,
  width = '75%',
  referencesToggle,
  onLoadingChange,
}) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { categories, isEditor, contextLoading } = useCategory();
  const { vertical_id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddViaLink, setShowAddViaLink] = useState(false);
  const [itemAdded, setItemAdded] = useState(0);
  const [activeButton, setActiveButton] = useState('Vertical Members');
  const { user } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const new_card_id = location.state?.new_card_id || {};
  let animationRef = useRef(JSON.parse(JSON.stringify(location.state?.animation || {})));
  let animationRefLink = useRef(false);

  const tab = searchParams.get('tab');

  // Sort options for this category (null until a category is configured in sortOptions.js).
  const sortConfig = getSortOptions(category);
  const sortSections = sortConfig
    ? [
        { label: 'General', options: sortConfig.general },
        { label: 'Category Specific', options: sortConfig.specific },
      ]
    : null;

  const activeButtonStyle = {
    textTransform: 'none',
    margin: '10px 10px 10px 0px',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    borderBottom: '2px solid',
    borderColor: 'primary.main',
    borderRadius: 0,
    paddingBottom: '4px',
  };
  const buttonStyle = {
    textTransform: 'none',
    margin: '10px 10px 10px 0px',
    color: 'black',
  };

  function capitalizeFirstLetter(string) {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  useEffect(() => {
    const getItems = async () => {
      if (category === 'papers') {
        document.title = 'Publications - The Simple Platform';
      } else if (category === 'grants') {
        document.title = 'Funding - The Simple Platform';
      } else {
        document.title = capitalizeFirstLetter(category) + ' - The Simple Platform';
      }
      const items = await fetchItems();
      if (items.errorStatus) {
        setLoading(false);
        navigate('/forbidden', { replace: true });
        return;
      }

      if (!contextLoading) {
        if (isEditor) {
          setItems(items.data);
        } else {
          setItems(
            items.data.filter((item) => {
              if (item?.verticals?.length) {
                return item.verticals.some(
                  (v) => (v.show === undefined ? true : v.show) && v.id === vertical_id
                );
              }

              return item.show;
            })
          );
        }
        setLoading(false);
      }

      if (itemAdded > 0) {
        animationRefLink.current = true;
      }

      if (category === 'people' && tab) {
        setActiveButton(tab);
      }
    };
    getItems();
    window.history.replaceState({}, '');
  }, [itemAdded, vertical_id, isEditor, contextLoading, navigate]);

  const displayedItems = useMemo(() => {
    if (category !== 'people') return items;

    if (activeButton === 'Vertical Members')
      return items.filter((p) => ['Creator', 'Editor', 'Member'].includes(p.type));
    if (activeButton === 'Authors') return items.filter((p) => p.type === 'author');
    if (activeButton === 'Inventors') return items.filter((p) => p.type === 'inventor');

    return items.filter(
      (p) => !['Creator', 'Editor', 'Member', 'author', 'inventor'].includes(p.type)
    );
  }, [items, activeButton, category]);

  const fetchItems = async () => {
    let id = vertical_id;
    const url = `${process.env.REACT_APP_FLASK_WEBSERVER}get_${category}?vertical_id=${id}`;

    try {
      const token = await user.getIdToken();
      const [itemsRes, bookmarksRes] = await Promise.all([
        fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}get_bookmarks_ids?vertical_id=${vertical_id}&category=${category}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (itemsRes.status === 403) {
        return { data: [], errorStatus: 403 };
      }

      const itemsData = await itemsRes.json();
      const bookmarksData = bookmarksRes.ok ? await bookmarksRes.json() : { bookmarks: [] };

      const bookmarks = new Set(bookmarksData.bookmarks.map((item) => item.item_id));

      const result = itemsData.data.map((item) => ({
        ...item,
        isBookmarked: bookmarks.has(item._id),
      }));

      return {
        data: result,
      };
    } catch (error) {
      console.error('Failed to fetch items or bookmarks:', error);
      return { data: [] };
    }
  };

  const addItem = async () => {
    setShowAddViaLink((prevValue) => !prevValue);
  };

  const toggleAddForm = (newState) => {
    setShowAddViaLink(newState);
  };

  const displayNewItem = () => {
    setItemAdded((prevState) => prevState + 1);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !isEditor) return;

    const reorderedItems = Array.from(displayedItems);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    if (category === 'people') {
      setItems((prevItems) => {
        const displayedIds = new Set(displayedItems.map((i) => i._id));
        let reorderedIndex = 0;

        return prevItems.map((item) => {
          if (displayedIds.has(item._id)) {
            return reorderedItems[reorderedIndex++];
          }
          return item;
        });
      });
    } else {
      setItems(reorderedItems);
    }

    await updateOrder(reorderedItems);
  };

  const getCollectionForPeopleTab = () => {
    if (activeButton === 'Authors') return 'authors';
    if (activeButton === 'Inventors') return 'inventors';
    if (activeButton === 'Vertical Members') return 'users';
    return 'people';
  };

  const updateOrder = async (items) => {
    if (!user) return;
    const collection = category === 'people' ? getCollectionForPeopleTab() : category;

    const item_ids = items.map((item) => ({ id: item._id }));
    const token = await user.getIdToken();

    const url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      'update_order?collection=' +
      collection +
      '&vertical_id=' +
      vertical_id;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(item_ids),
    });
    await res.json();
    return res.status;
  };

  // Apply a chosen sort option, then persist the resulting order the same way a
  // manual drag does (reuses the `order` field via updateOrder). Owner/editor only.
  const handleSort = async (option) => {
    if (!isEditor) return;

    const sortedItems = [...displayedItems].sort(option.comparator);

    if (category === 'people') {
      // People render a filtered subset per tab; splice the sorted subset back in place.
      setItems((prevItems) => {
        const displayedIds = new Set(displayedItems.map((i) => i._id));
        let sortedIndex = 0;
        return prevItems.map((item) =>
          displayedIds.has(item._id) ? sortedItems[sortedIndex++] : item
        );
      });
    } else {
      setItems(sortedItems);
    }

    try {
      const status = await updateOrder(sortedItems);
      if (status === 200) {
        toast.success(`Sorted by ${option.label}`, { className: 'toast', autoClose: 4000 });
      } else {
        toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
      }
    } catch (error) {
      console.error('Failed to save sort order:', error);
      toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
    }
  };

  const displayAnimation = (id) => {
    if (id == new_card_id && animationRef.current) {
      animationRef.current = false;
      return true;
    }
    return false;
  };

  const displayAnimationLink = () => {
    if (animationRefLink.current) {
      animationRefLink.current = false;
      return true;
    }
    return false;
  };

  const handleBookmarkToggle = async (event, itemId, category) => {
    event.stopPropagation();

    setItems(
      items.map((item) =>
        item._id === itemId ? { ...item, isBookmarked: !item.isBookmarked } : item
      )
    );

    const item = items.find((item) => item._id === itemId);

    let itemCategory;
    switch (item.type) {
      case 'author':
        itemCategory = 'authors';
        break;
      case 'inventor':
        itemCategory = 'inventors';
        break;
      case 'Creator':
        itemCategory = 'users';
        break;
      case 'Editor':
        itemCategory = 'users';
        break;
      case 'Member':
        itemCategory = 'users';
        break;
      default:
        itemCategory = category;
    }

    try {
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}toggle_bookmark`;
      const token = await user.getIdToken();

      let id = vertical_id;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.uid,
          item_id: itemId,
          category: itemCategory,
          vertical_id: id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to toggle:', error);
    }
  };

  // Sync a card's visibility toggle back into `items` so visibility-based sorting
  // (and any other state derived from `show`) uses the fresh value immediately,
  // without waiting for a refetch. Members store visibility per-vertical.
  const handleVisibilityChange = (itemId, show) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item._id !== itemId) return item;
        // Comparators read root `show` for every category (the backend flattens a
        // member's per-vertical visibility onto it), so always update root `show`
        // and keep the per-vertical entry in sync when present.
        const updated = { ...item, show };
        if (Array.isArray(item.verticals) && item.verticals.length) {
          updated.verticals = item.verticals.map((v) =>
            v.id === vertical_id ? { ...v, show } : v
          );
        }
        return updated;
      })
    );
  };

  // Let a parent (e.g. PublicationsPage) coordinate a single page-level spinner.
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  return loading ? (
    <Spinner />
  ) : (
    <div style={{ width }}>
      <div className="papers">
        <div>
          <div className="list-header-row">
            <h2>
              {heading}
              {isEditor && category !== 'tutorials' && (
                <FontAwesomeIcon
                  icon={faCirclePlus}
                  color={theme.palette.primary.main}
                  className={'add-button'}
                  onClick={() => {
                    addItem();
                  }}
                ></FontAwesomeIcon>
              )}
              {isEditor && category !== 'tutorials' && (
                <span style={{ marginLeft: '16px', verticalAlign: 'middle' }}>
                  <WatchGuideLink
                    videoId="adding-resources-manually"
                    label="Watch Guide: Adding Resources"
                  />
                </span>
              )}
            </h2>

            {/* Collapsed References toggle, sits above the first card on the right. */}
            {referencesToggle && <div>{referencesToggle}</div>}

            {category === 'jobs' && (
              <p className="list-header-note">
                These results are intentionally limited, focused primarily on the vertical
                creator&apos;s niche area of research interest. Niche hallucinations sometimes
                occur; we are working on that. Users will want to utilize other, more generalized
                resources to find more general results and add them to their TSP workbench using the
                &quot;+&quot; button.
              </p>
            )}

            {category === 'people' && (
              <p className="list-header-note">
                TSP currently identifies relevant people by starting with authors of publications
                found through the vertical search and adding vertical members. Future enhancements
                may introduce ORCID for identity persistence and group people for clarity, without
                ranking them.
              </p>
            )}
          </div>

          {showAddViaLink && (
            <AddViaLink
              category={category}
              toggleAddForm={toggleAddForm}
              displayNewItem={displayNewItem}
            />
          )}
        </div>

        {category === 'people' && (
          <div>
            <Button
              variant="text"
              disableRipple={true}
              sx={activeButton === 'Vertical Members' ? activeButtonStyle : buttonStyle}
              onClick={() => setActiveButton('Vertical Members')}
            >
              Vertical Members
            </Button>
            <Button
              variant="text"
              disableRipple={true}
              sx={activeButton === 'Authors' ? activeButtonStyle : buttonStyle}
              onClick={() => setActiveButton('Authors')}
            >
              Authors
            </Button>
            <Button
              variant="text"
              disableRipple={true}
              sx={activeButton === 'Inventors' ? activeButtonStyle : buttonStyle}
              onClick={() => setActiveButton('Inventors')}
            >
              Inventors
            </Button>
            <Button
              variant="text"
              disableRipple={true}
              sx={activeButton === 'Others' ? activeButtonStyle : buttonStyle}
              onClick={() => setActiveButton('Others')}
            >
              Others
            </Button>
          </div>
        )}

        {category === 'tutorials' && (
          <>
            <p
              style={{
                textAlign: 'justify',
                textJustify: 'inter-word',
                fontSize: '13px',
                lineHeight: '1.3',
                color: 'gray',
              }}
            >
              <strong>Tutorials</strong> are part of TSP’s future service roadmap and will come in
              two forms:
              <br></br>
              <br></br>
              <strong>1. Platform-wide tutorials</strong>
              <br></br> Available to all TSP members and focused on general-interest topics.
              <br></br>
              <br></br>
              <strong>2. Vertical-specific tutorials</strong>
              <br></br> Available only to members within a given vertical. Vertical owners and
              editors will create these tutorials and retain full rights. <br></br>
              <br></br>PDF uploads are supported today, and we are exploring additional formats.
              Ideas and feedback are welcome at{' '}
              <a href="mailto:tutorials@simple-platform.com">tutorials@simple-platform.com</a>
            </p>
          </>
        )}

        {displayedItems.length > 0 ? (
          <>
            {isEditor && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '8px 0',
                }}
              >
                <div>
                  {sortConfig && displayedItems.length > 1 && (
                    <SortByMenu sections={sortSections} onSelect={handleSort} />
                  )}
                </div>
                <WatchGuideLink
                  videoId="managing-resource-actions"
                  label="Watch Guide: Managing Resources"
                />
              </div>
            )}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="cards">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {displayedItems.map((item, index) => (
                      <Draggable
                        key={item._id}
                        draggableId={item._id}
                        index={index}
                        isDragDisabled={!isEditor}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...(isEditor ? provided.dragHandleProps : {})}
                          >
                            <Card
                              key={item._id}
                              category={category}
                              details={item}
                              editor={isEditor}
                              cardHeader={cardHeader}
                              icons={icons}
                              urlIcons={urlIcons}
                              dotColor={dotColor}
                              vertical_id={vertical_id}
                              animation={
                                (index == 0 && displayAnimationLink()) || displayAnimation(item._id)
                              }
                              handleBookmarkToggle={(event) =>
                                handleBookmarkToggle(event, item._id, category)
                              }
                              uid={user.uid}
                              onDeleteSuccess={(id) =>
                                setItems((prev) => prev.filter((i) => i._id !== id))
                              }
                              onVisibilityChange={handleVisibilityChange}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        ) : (
          <WhatsComing category={category} />
        )}
      </div>
    </div>
  );
};

export default List;
